import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Input, Modal, message} from 'antd'
import {resolveSkus} from '../../../../services/api/apiProductionPlanning'
import {errorMessage} from '../../helpers/apiError'

const {TextArea} = Input

/**
 * Hộp thoại M1 — Dán danh sách SKU (§6).
 *
 * Kết quả trả về ba nhóm và báo ở **ba mức khác nhau** (§0.3):
 * - thêm được (xanh lá)
 * - SKU đang tắt: **vẫn cho thêm**, chỉ cảnh báo vàng (V-11) — dữ liệu cũ vẫn cần định mức
 * - không có trong danh mục: bị loại, báo đỏ (V-10)
 */
function PasteSkuModal({visible, onCancel, onAdd}) {
    const [text, setText] = useState('')
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)

    /** Mỗi lần mở là một lượt dán mới — không để ô nhập và bảng kết quả của lần trước còn lại. */
    useEffect(() => {
        if (!visible) return

        setText('')
        setResult(null)
    }, [visible])

    const handleOk = async () => {
        const skuIds = text.split(/[\n,;|]+/).map((item) => item.trim()).filter(Boolean)

        if (!skuIds.length) return

        setLoading(true)

        try {
            const resolved = await resolveSkus(skuIds)

            // SKU đang tắt vẫn được thêm vào nhóm cùng với SKU hợp lệ.
            onAdd([...(resolved.accepted || []), ...(resolved.inactive || [])], resolved)
            setText('')

            /*
             * Chỉ đóng hộp thoại khi KHÔNG có gì phải báo.
             *
             * Trước đây `onAdd` làm parent đóng hộp thoại ngay trong cùng batch với `setResult`,
             * mà khối kết quả lại nằm BÊN TRONG <Modal> — nên cả ba mức thông báo (§0.3) không bao
             * giờ hiển thị: dán 50 mã mà 12 mã không có trong danh mục (V-10) hoặc đang tắt (V-11)
             * thì người dùng vẫn tưởng đã thêm đủ 50.
             */
            const hasReport = !!(resolved.notFound || []).length || !!(resolved.inactive || []).length

            if (hasReport) {
                setResult(resolved)
            } else {
                onCancel()
            }
        } catch (e) {
            // Không có catch thì bấm "Thêm vào nhóm" mà thất bại sẽ KHÔNG có phản hồi nào:
            // `Modal.onOk` của antd không bắt rejection, người dùng bấm lại vô ích.
            message.error(errorMessage(e) || 'Không phân giải được danh sách SKU. Vui lòng thử lại')
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        setResult(null)
        onCancel()
    }

    return (
        <Modal
            title="Dán danh sách SKU"
            visible={visible}
            onCancel={handleCancel}
            onOk={handleOk}
            confirmLoading={loading}
            okText="Thêm vào nhóm"
            cancelText="Hủy"
        >
            <div className="VcHint mb-2">
                Mỗi mã một dòng hoặc cách nhau bằng dấu phẩy. Hệ thống bỏ trùng và kiểm tra SKU tồn
                tại, đang bật.
            </div>
            <TextArea rows={5} value={text} onChange={(event) => setText(event.target.value)}/>

            {result && (
                <div className="VcPasteResult">
                    {!!(result.accepted || []).length && (
                        <div className="VcSuccess">Đã thêm {result.accepted.length} mã.</div>
                    )}
                    {!!(result.notFound || []).length && (
                        <div className="VcDanger">
                            {result.notFound.length} mã bị loại: {result.notFound.join(', ')} — không có
                            trong danh mục.
                        </div>
                    )}
                    {!!(result.inactive || []).length && (
                        <div className="VcWarning">
                            {result.inactive.length} mã đang ở trạng thái Tắt: {result.inactive.join(', ')}.
                        </div>
                    )}
                </div>
            )}
        </Modal>
    )
}

PasteSkuModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
}

export default PasteSkuModal
